import React, { useState, memo, useEffect } from 'react';
import { Button, Input, Typography, Form, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { VehicleEnrichment, type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';
import { useNotification } from '@/shared/hooks/useNotification';
import { useOptionalSearchContext } from '../context/SearchContext';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

interface ProblemInputProps {
  initialProblem: string;
  initialVehicle: VehicleSelectionValue;
  onSubmit: (problem: string, vehicle: VehicleSelectionValue) => void;
}

export const ProblemInput: React.FC<ProblemInputProps> = memo(({ 
  initialProblem, 
  initialVehicle,
  onSubmit
}) => {
  const [searchParams] = useSearchParams();
  const [problem, setProblem] = useState(initialProblem);
  const [vehicle, setVehicle] = useState<VehicleSelectionValue>(initialVehicle);
  const { t } = useTranslation();
  const { contextHolder, notification } = useNotification();
  const searchContext = useOptionalSearchContext();

  useEffect(() => {
    // Read query parameters if they exist
    const problemParam = searchParams.get('problem');
    const manufacturerParam = searchParams.get('manufacturer');
    const modelSeriesParam = searchParams.get('modelSeries');
    const engineBaseTypeParam = searchParams.get('engineBaseType');
    const modelYearParam = searchParams.get('modelYear');
    const euroCodeParam = searchParams.get('euroCode');

    if (problemParam) {
      setProblem(problemParam);
    }

    const vehicleFromParams: VehicleSelectionValue = {};
    if (manufacturerParam) vehicleFromParams.manufacturer = manufacturerParam;
    if (modelSeriesParam) vehicleFromParams.modelSeries = modelSeriesParam;
    if (engineBaseTypeParam) vehicleFromParams.engineBaseType = engineBaseTypeParam;
    if (modelYearParam) vehicleFromParams.modelYear = parseInt(modelYearParam, 10);
    if (euroCodeParam) vehicleFromParams.euroCode = euroCodeParam;

    if (Object.keys(vehicleFromParams).length > 0) {
      setVehicle(vehicleFromParams);
    }
  }, [searchParams]);

  // Update SearchContext whenever local form state changes (only in demo mode)
  useEffect(() => {
    if (searchContext) {
      // Debounce the update to avoid too many re-renders
      const timeoutId = setTimeout(() => {
        searchContext.setProblemDescription(problem);
        searchContext.setVehicleData(vehicle);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [problem, vehicle, searchContext]);

  const handleSubmit = () => {
    if (isValid) {
      onSubmit(problem.trim(), vehicle);
    } else {
      showValidationError();
    }
  };

  const showValidationError = () => {
    if (!problem.trim()) {
      notification.warning(
        t('demo.problemInput.validationTitle'),
        t('demo.problemInput.problemRequired')
      );
    } else if (!hasVehicleInfo) {
      notification.warning(
        t('demo.problemInput.validationTitle'),
        t('demo.problemInput.vehicleInfoRequired')
      );
    } else if (!isValidModelSelection) {
      notification.warning(
        t('demo.problemInput.validationTitle'),
        t('demo.problemInput.manufacturerRequiredForModel')
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  const hasVehicleInfo = vehicle.manufacturer || vehicle.modelSeries || vehicle.engineBaseType || vehicle.modelYear || vehicle.euroCode;
  const isValidModelSelection = !vehicle.modelSeries || vehicle.manufacturer; // Model series requires manufacturer
  const isValid = problem.trim() && hasVehicleInfo && isValidModelSelection;

  return (
    <>
      {contextHolder}
      <div style={{ textAlign: 'left' }}>
        <Title level={4}>
          {t('demo.problemInput.title')}
        </Title>
      
      <Paragraph style={{color: '#666' }}>
        {t('demo.problemInput.subtitle')}
      </Paragraph>

      <Form layout="vertical">
        <Form.Item>
          <TextArea
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('demo.problemInput.placeholder')}
            rows={6}
            maxLength={512}
            showCount
            style={{ 
              fontSize: 14,
              borderRadius: 8
            }}
          />
        </Form.Item>
      </Form>

      <Divider style={{ margin: '32px 0' }} />

      <div style={{ textAlign: 'left' }}>
        <Title level={4}>
          {t('demo.problemInput.vehicleInfoTitle')}
        </Title>
        
        <Paragraph style={{ color: '#666' }}>
          {t('demo.problemInput.vehicleInfoSubtitle')}
        </Paragraph>
        <VehicleEnrichment
          value={vehicle}
          onChange={setVehicle}
        />
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
        <Button 
          type="primary" 
          size="large"
          onClick={handleSubmit}
          style={{ 
            minWidth: 160,
            height: 48,
            borderRadius: 8,
            fontSize: 16,
            flex: 1
          }}
        >
          {t('demo.problemInput.findSolutions')}
        </Button>
      </div>
      </div>
    </>
  );
});

ProblemInput.displayName = 'ProblemInput';